import Image from "next/image";
import dynamic from "next/dynamic";
import BreadCrumbs from "../BreadCrumbs";
import { getReadableImageUrl } from "@/lib/directus";
import { getGalleryPatientUrl } from "@/lib/gallery";

const Link = dynamic(() => import("@/components/ui/Link"));

interface ProcedureSectionProps {
  data?: any;
}

export default function ProcedureSection({ data }: ProcedureSectionProps) {
  // Extract procedure name from the data object
  const procedureName = data?.procedure?.title || "Procedure Gallery";
  const procedureSlug = data?.procedureSlug || "";
  
  return (
    <section className="w-full bg-white px-5 py-[1.625rem] md:px-20 md:pt-10 md:pb-[7.5rem]">
      {/* Breadcrumb */}
      <div className="text-primary text-xs md:text-base font-greycliff mb-[1.125rem] md:mb-4">
        <BreadCrumbs
          segments={[{ title: procedureName }]}
        />
      </div>

      {/* Header */}
      <div className="flex justify-between items-center mb-[1.125rem] py-8">
        <h2 className="text-primary text-[2.5rem] font-light font-essonnes leading-[3rem]">
          {procedureName}
        </h2>
        <Link
          href="/gallery/"
          className="hidden text-primary md:flex h-[4.375rem] px-[3.125rem] rounded-[3.125rem] border border-primary items-center hover:bg-primary hover:text-white transition-colors"
        >
          <span className="text-inherit text-xl font-greycliff leading-7">
            Back to Gallery
          </span>
        </Link>
      </div>

      {/* Patient Grid */}
      <div className="flex flex-col gap-[1.875rem] md:gap-4">
        {data.patients && data.patients.map(
          (patient, index: number) =>
            patient.photos &&
            patient.photos.length > 0 && (
              <div key={index} className="bg-[#efefef] rounded-2xl p-4 md:px-[3.25rem] md:pt-9 md:pb-4">
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <Link
                    href={`${getGalleryPatientUrl(procedureSlug, patient.id) || "#"}#patient`}
                    className="flex flex-col md:flex-row gap-4 w-full"
                  >
                    {patient.photos.slice(0, 3).map((photo, photoIndex: number) => (
                      <div 
                        key={photoIndex} 
                        className={`flex flex-col gap-2 ${
                          patient.photos.length === 1 
                            ? 'md:w-[32%] md:mx-auto' 
                            : `flex-1 ${photoIndex > 0 ? 'hidden lg:flex' : ''}`
                        }`}
                      >
                        <div className="relative w-full h-[6.75rem] md:h-[8.875rem]">
                          <Image
                            src={getReadableImageUrl(
                              photo.directus_files_id.id,
                              photo.directus_files_id.filename_disk
                            )}
                            alt={`Before & After Procedure - Photo ${photoIndex + 1}`}
                            fill
                            className="rounded-lg object-cover"
                            sizes="(max-width: 768px) 100vw, 33vw"
                            loading={index === 0 && photoIndex === 0 ? 'eager' : 'lazy'}
                            priority={index === 0 && photoIndex === 0}
                            quality={90}
                          />
                        </div>
                        <div className="flex justify-between px-4 md:px-2.5">
                          <span className="text-primary text-sm font-medium font-greycliff">BEFORE</span>
                          <span className="text-primary text-sm font-medium font-greycliff">AFTER</span>
                        </div>
                      </div>
                    ))}
                  </Link>
                  <Link
                    href={`${getGalleryPatientUrl(procedureSlug, patient.id) || "#"}#patient`}
                    className="text-primary flex md:ml-[2.625rem] h-[3.5rem] md:h-[4.375rem] px-[2rem] md:px-[3.125rem] rounded-[3.125rem] border border-primary hover:bg-primary hover:text-white transition-colors whitespace-nowrap items-center justify-center"
                  >
                    <span className="text-inherit text-sm md:text-xl font-greycliff leading-6 md:leading-7">
                      View Patient Details
                    </span>
                  </Link>
                </div>
              </div>
            )
        )}
      </div>

      {/* Mobile Footer Buttons */}
      <div className="md:hidden flex flex-col gap-4 mt-[1.875rem]">
        <Link
          href="/gallery/"
          className="h-[4.375rem] px-[3.125rem] rounded-[3.125rem] border border-primary hover:bg-primary hover:text-white transition-colors flex items-center justify-center"
        >
          <span className="text-inherit text-xl font-greycliff leading-7">
            Back to Gallery
          </span>
        </Link>
      </div>
    </section>
  );
}